/**
 * Wraps an async action with standard loading + error handling so handlers
 * stop re-implementing the same try / catch / finally pattern.
 *
 *   await runAsync(() => auth.signInWithEmail(email, password), {
 *     setError,
 *     setLoading: setSubmitting,
 *   })
 *
 * Conventions:
 * - `setError?.(null)` runs first so a retry clears the previous error
 * - `setLoading?.(true)` at start, `setLoading?.(false)` in `finally`
 * - If the action returns `{ error: string }` (our context-wrapper contract),
 *   that string is surfaced via `setError`
 * - If the action throws, `errorMap(err)` produces the message, else
 *   `err.message`, else `fallbackError`
 * - Always returns the action's result so the caller can inspect it (e.g.
 *   check `result.error` to skip a navigation step, or use the returned data)
 *
 * Not tied to auth specifically — any handler with this shape can use it.
 */
export async function runAsync(action, opts = {}) {
  const {
    setError,
    setLoading,
    errorMap,
    fallbackError = 'Something went wrong. Please try again.',
  } = opts

  setError?.(null)
  setLoading?.(true)
  try {
    const result = await action()
    if (result && typeof result.error === 'string' && result.error) {
      setError?.(result.error)
    }
    return result
  } catch (err) {
    const message = errorMap ? errorMap(err) : err?.message || fallbackError
    setError?.(message)
    return { error: message }
  } finally {
    setLoading?.(false)
  }
}
